import {
  CashbackTriple,
  NamedTriple,
  RevenueSharingMonth,
  RevenueSharingReportResponse,
} from "../api/revenueSharing";

export type Granularity = "monthly" | "quarterly";

export interface PeriodColumn {
  key: string; // "2026-03" or "2026-Q1"
  label: string;
  monthCount: number; // quarters built from fewer than 3 months in range are partial
}

export interface ReportRow {
  key: string;
  name: string;
  cells: Record<string, CashbackTriple>;
}

export interface ReportSection {
  id: string;
  label: string;
  rows: ReportRow[]; // empty for single-bucket sections (Cardlinking, Webshop)
  subtotal: Record<string, CashbackTriple>;
}

export interface ReportTable {
  periods: PeriodColumn[]; // newest first
  sections: ReportSection[];
  grandTotal: Record<string, CashbackTriple>;
}

export const ZERO_TRIPLE: CashbackTriple = {
  totalCashback: 0,
  userCashback: 0,
  revenue: 0,
  transactionCount: 0,
};

export const quarterKeyOf = (month: string): string => {
  const quarter = Math.floor((Number(month.slice(5)) - 1) / 3) + 1;
  return `${month.slice(0, 4)}-Q${quarter}`;
};

export const sumTriples = (triples: CashbackTriple[]): CashbackTriple =>
  triples.reduce(
    (acc, t) => ({
      totalCashback: acc.totalCashback + t.totalCashback,
      userCashback: acc.userCashback + t.userCashback,
      revenue: acc.revenue + t.revenue,
      transactionCount: acc.transactionCount + t.transactionCount,
    }),
    ZERO_TRIPLE
  );

const monthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${names[Number(monthNumber) - 1]} ${year}`;
};

const quarterLabel = (quarterKey: string): string =>
  `${quarterKey.slice(5)} ${quarterKey.slice(0, 4)}`;

export const buildPeriods = (
  months: string[],
  granularity: Granularity
): PeriodColumn[] => {
  const sorted = [...months].sort();
  if (granularity === "monthly") {
    return sorted
      .map((month) => ({ key: month, label: monthLabel(month), monthCount: 1 }))
      .reverse();
  }

  const byQuarter = new Map<string, number>();
  for (const month of sorted) {
    const key = quarterKeyOf(month);
    byQuarter.set(key, (byQuarter.get(key) ?? 0) + 1);
  }
  return Array.from(byQuarter.entries())
    .map(([key, monthCount]) => ({ key, label: quarterLabel(key), monthCount }))
    .reverse();
};

const periodOf = (month: string, granularity: Granularity): string =>
  granularity === "monthly" ? month : quarterKeyOf(month);

const aggregateSingle = (
  months: RevenueSharingMonth[],
  pick: (m: RevenueSharingMonth) => CashbackTriple,
  granularity: Granularity
): Record<string, CashbackTriple> => {
  const cells: Record<string, CashbackTriple> = {};
  for (const month of months) {
    const period = periodOf(month.month, granularity);
    cells[period] = sumTriples([cells[period] ?? ZERO_TRIPLE, pick(month)]);
  }
  return cells;
};

const aggregateNamed = (
  months: RevenueSharingMonth[],
  pick: (m: RevenueSharingMonth) => NamedTriple[],
  granularity: Granularity
): ReportRow[] => {
  const rows = new Map<string, ReportRow>();
  for (const month of months) {
    const period = periodOf(month.month, granularity);
    for (const item of pick(month)) {
      let row = rows.get(item.key);
      if (!row) {
        row = { key: item.key, name: item.name, cells: {} };
        rows.set(item.key, row);
      }
      row.cells[period] = sumTriples([
        row.cells[period] ?? ZERO_TRIPLE,
        item.totals,
      ]);
    }
  }
  return Array.from(rows.values());
};

const subtotalOf = (
  rows: ReportRow[],
  periods: PeriodColumn[]
): Record<string, CashbackTriple> => {
  const subtotal: Record<string, CashbackTriple> = {};
  for (const period of periods) {
    subtotal[period.key] = sumTriples(
      rows.map((row) => row.cells[period.key] ?? ZERO_TRIPLE)
    );
  }
  return subtotal;
};

export const buildReportTable = (
  report: RevenueSharingReportResponse,
  granularity: Granularity
): ReportTable => {
  const months = report.months;
  const periods = buildPeriods(
    months.map((m) => m.month),
    granularity
  );

  const cardlinking = aggregateSingle(months, (m) => m.cardlinking, granularity);
  const webshop = aggregateSingle(months, (m) => m.webshop, granularity);
  const affiliateRows = aggregateNamed(months, (m) => m.affiliateNetworks, granularity);
  const apiRows = aggregateNamed(months, (m) => m.apiPartners, granularity);
  const reportingRows = aggregateNamed(months, (m) => m.reportingPartners, granularity);

  const sections: ReportSection[] = [
    { id: "cardlinking", label: "Cardlinking", rows: [], subtotal: cardlinking },
    {
      id: "affiliate",
      label: "Affiliate",
      rows: affiliateRows,
      subtotal: subtotalOf(affiliateRows, periods),
    },
    {
      id: "api",
      label: "API",
      rows: apiRows,
      subtotal: subtotalOf(apiRows, periods),
    },
    {
      id: "reporting",
      label: "Reporting",
      rows: reportingRows,
      subtotal: subtotalOf(reportingRows, periods),
    },
    { id: "webshop", label: "Webshop", rows: [], subtotal: webshop },
  ];

  return {
    periods,
    sections,
    grandTotal: aggregateSingle(months, (m) => m.total, granularity),
  };
};
